<template>
  <div>
    <!-- 予約情報表示セクション -->
    <div ref="target" class="flex flex-col gap-y-4">
      <div v-if="props.isDoneReservation" class="justify-between md:flex">
        <p class="text-[22px]">Your Booking</p>
      </div>
      <!-- 体験タイトルと画像表示セクション -->
      <TitleWithImage
        text-style="text-[16px] md:text-[17px] font-light leading-[22px] tracking-wider"
        :img="thumbnailImageUrl"
        :on-click-image="onClickImage"
        :slug="props.slug"
        :title="props.experienceEvent.experience.name"
      />
      <ul v-if="props.isDoneReservation" class="gap-y-4 md:flex md:flex-col md:text-[17px]">
        <li class="flex gap-2">
          <img alt="アイコン" class="mt-[1.6px] h-6 w-6" :src="calendarIconUrl" />
          {{ firstPreferredDateTime }}
        </li>
        <li class="flex gap-2">
          <img alt="アイコン" class="h-6 w-6" :src="personIconUrl" />
          {{ participantsLabel }}
        </li>
      </ul>
      <div v-if="shouldShowPrice">
        <TotalPriceSectionWithGuestType
          v-if="props.experienceEvent.timeSlotId"
          :applied-code="reservationStep2Form.code"
          :experience-event="props.experienceEvent"
          :pre-reservation-price-with-guest-type="props.preReservationPriceWithGuestType"
        >
          <template v-if="$slots.beforeTotal" #beforeTotal>
            <slot name="beforeTotal" />
          </template>
        </TotalPriceSectionWithGuestType>
        <TotalPriceSection
          v-else
          :applied-code="reservationStep2Form.code"
          :number-of-participants="reservationStep1Form.numOfParticipants"
          :pre-reservation-price="props.preReservationPrice"
          :selected-experience-event="props.experienceEvent"
          :selected-experience-event-options="reservationStep2Form.experienceEventOptions"
          :selected-interpreter-option="reservationStep2Form.experienceEventInterpreterOption"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import calendarIconUrl from '@/assets/icon/calendar/gray.svg';
import personIconUrl from '@/assets/icon/person/gray.svg';
import { TitleWithImage } from '@/wabunka-src/common/components';
import TotalPriceSection from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSection.vue';
import TotalPriceSectionWithGuestType from '@/wabunka-src/common/features/Experience/components/TotalPriceSection/TotalPriceSectionWithGuestType.vue';
import type { WabunkaReservationFormStep1, WabunkaReservationFormStep2, ExperiencePreReservationPriceViewModel } from '@/wabunka-src/features/Experience';
import { buildWabunkaExperienceEventPreferredDateTime, type WabunkaExperienceEventViewModel } from '@/wabunka-src/features/Experience/models/experienceEvent';
import { formatGuestCountsLabel } from '@/wabunka-src/features/Experience/models/experienceEventFee';
import type { ExperiencePreReservationPriceWithGuestTypeViewModel } from '@/wabunka-src/features/Experience/subFeatures/Reservation/models/wabunkaExperiencePreReservationPrice';
const props = defineProps<{
  slug: string;
  experienceEvent: WabunkaExperienceEventViewModel;
  reservationStep1Form: WabunkaReservationFormStep1;
  reservationStep2Form: WabunkaReservationFormStep2;
  isDoneReservation: boolean;
  preReservationPrice: ExperiencePreReservationPriceViewModel;
  preReservationPriceWithGuestType?: ExperiencePreReservationPriceWithGuestTypeViewModel;
  onClickImage: () => void;
}>();

const thumbnailImageUrl = computed(() => {
  const experienceImages = props.experienceEvent.experience.images;
  // 運用上体験画像が存在しない場合は起きないが、システム上は起こり得るのでチェック
  return experienceImages.length > 0 ? experienceImages[0].imageUrl : '';
});

const shouldShowMap = ref(false);
const target = ref<HTMLElement | null>(null);

const firstPreferredDateTime = computed(() => buildWabunkaExperienceEventPreferredDateTime(props.experienceEvent));
onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) shouldShowMap.value = true;
  });
  if (target.value) observer.observe(target.value);
});

const shouldShowPrice = computed(() => props.experienceEvent.experience.isPublicPrice);

const participantsLabel = computed(() => {
  if (props.experienceEvent.timeSlotId && props.reservationStep1Form.guestCounts && props.experienceEvent.experienceEventFees?.length) {
    const label = formatGuestCountsLabel(props.reservationStep1Form.guestCounts, props.experienceEvent.experienceEventFees);
    if (label) return label;
  }
  const n = props.reservationStep1Form.numOfParticipants;
  return `${n} participant${n !== 1 ? 's' : ''}`;
});
</script>
